import React, { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { type BrandColorVariants } from "theme/colors";

/**
 * ColorTheme controls the theming of the header & navigation so that it looks seamless with the rest of the page.
 *
 * The theme should be updated only when a full width section of the page changes background color (ex. plates).
 * Do not update the theme for small sections of the page, or components that are not full width (ex. quotes).
 */

export type BrandColor = keyof BrandColorVariants;
type ColorShade = "50" | "150" | "900" | "950";
export type ThemeBgColor = `new.${BrandColor}.${ColorShade}` | "white";
export type Theme = "dark" | "light";

type ColorThemeContextType = {
  /**
   * The current theme of the navigation. Can be "dark" or "light".
   * This is used to determine the text color of the navigation items.
   */
  theme: Theme;

  /**
   * The current background color of the navigation.
   */
  bgColor: ThemeBgColor;

  /**
   * A function to set the theme of the navigation.
   */
  setTheme: (theme: Theme) => void;

  /**
   * A function to set the background color of the navigation.
   */
  setBgColor: (color: ThemeBgColor) => void;
};

export type ColorThemePageProps = Pick<ColorThemeContextType, "theme" | "bgColor">;

const defaultContextValue: ColorThemeContextType = {
  theme: "light",
  bgColor: "white",
  setTheme: () => null,
  setBgColor: () => null,
};

export const ColorTheme = createContext<ColorThemeContextType>(defaultContextValue);

export const useColorTheme = () => useContext(ColorTheme);

type ColorThemeProviderProps = {
  initialTheme: Theme;
  initialBgColor: ThemeBgColor;
  children: ReactNode;
};

export function ColorThemeProvider({ initialTheme, initialBgColor, children }: ColorThemeProviderProps) {
  const [theme, setTheme] = useState(initialTheme);
  const [bgColor, setBgColor] = useState(initialBgColor);

  const value = useMemo(
    () => ({
      theme,
      bgColor,
      setTheme,
      setBgColor,
    }),
    [theme, bgColor],
  );

  return <ColorTheme.Provider value={value}>{children}</ColorTheme.Provider>;
}
